import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IDLE_MS = 30 * 60 * 1000;
const WARN_MS = IDLE_MS - 2 * 60 * 1000;
const EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];

export const useIdleLogout = (navigate) => {
  const last = useRef(Date.now());
  const warned = useRef(false);

  useEffect(() => {
    const touch = () => {
      last.current = Date.now();
      if (warned.current) {
        warned.current = false;
        toast.dismiss("idle-warning");
      }
    };
    const expire = async () => {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
      navigate("/admin/login?expired=1", { replace: true });
    };
    const check = () => {
      const idle = Date.now() - last.current;
      if (idle >= IDLE_MS) return expire();
      if (idle >= WARN_MS && !warned.current) {
        warned.current = true;
        toast.warning("You'll be signed out in 2 minutes for security. Move the mouse or tap to stay signed in.", {
          id: "idle-warning",
          duration: Infinity,
        });
      }
      return undefined;
    };
    const interceptor = axios.interceptors.response.use(undefined, (err) => {
      if (err.response?.status === 401 && !err.config?.url?.includes("/auth/")) expire();
      return Promise.reject(err);
    });
    EVENTS.forEach((e) => window.addEventListener(e, touch, { passive: true }));
    document.addEventListener("visibilitychange", check);
    const timer = setInterval(check, 15000);
    return () => {
      EVENTS.forEach((e) => window.removeEventListener(e, touch));
      document.removeEventListener("visibilitychange", check);
      clearInterval(timer);
      axios.interceptors.response.eject(interceptor);
      toast.dismiss("idle-warning");
    };
  }, [navigate]);
};
